import { z } from "zod";

import {
  requiredWithoutDefault,
  ThreadVisibility,
  type TemplateVariable,
} from "@/shared/domain/agents";

import { buildAnalysisScope } from "./launchAnalysisScope";

/**
 * The launch form's schema. `values` is a record by name that leaves out a
 * variable nobody set, so the server applies its default; the window's raw
 * bounds become `analysisScope` on output, null when both are empty.
 *
 * The window is checked in the refinement, not the transform: zod skips the
 * transform once a refinement reports, so a bad window would stay hidden until
 * every variable was filled in.
 */
export function makeLaunchFormSchema(variables: readonly TemplateVariable[]) {
  const mustHave = requiredWithoutDefault(variables);
  return z
    .object({
      values: z.record(z.string(), z.string()),
      visibility: z.enum(ThreadVisibility),
      window: z.object({
        mode: z.enum(["datetime", "iso", "ns"]),
        start: z.string(),
        end: z.string(),
      }),
    })
    .superRefine((form, ctx) => {
      for (const name of mustHave) {
        if ((form.values[name] ?? "").trim() === "") {
          ctx.addIssue({
            code: "custom",
            path: ["values", name],
            message: "Fill this in to launch.",
          });
        }
      }
      const { error } = buildAnalysisScope(
        form.window.start,
        form.window.end,
        form.window.mode,
      );
      if (error !== null) {
        // On the end, the bound a reversed window is fixed at.
        ctx.addIssue({
          code: "custom",
          path: ["window", "end"],
          message: error,
        });
      }
    })
    .transform((form) => ({
      ...form,
      analysisScope: buildAnalysisScope(
        form.window.start,
        form.window.end,
        form.window.mode,
      ).scope,
    }));
}

export type LaunchFormValues = z.input<ReturnType<typeof makeLaunchFormSchema>>;
export type LaunchFormOutput = z.output<
  ReturnType<typeof makeLaunchFormSchema>
>;
